<template>
  <div
    v-if="context.request.user?.patient_profile || !context.request.user.is_authenticated"
    data-qa="favourite_btn"
    :data-qa-id="targetId"
    :data-qa-type="type"
    class="d-flex align-center justify-center cursor-pointer bg-gray-0"
    :style="size ? {
      width: `${size}px`,
      height: `${size}px`,
    } : undefined"
    @click="handleFavouriteClick"
  >
    <v-progress-circular
      v-if="isLoading"
      :size="20"
      width="2"
      color="primary"
      indeterminate
    />
    <span
      v-else
      class="ui-icon-fz-medium"
      :class="favouriteIconClass"
    />
    <CoreDialog
      v-model="isOpenedNeedLogin"
      data-qa="favourite_btn_need_login_modal"
      size="lg"
      :title="t('Необходимо войти в личный кабинет', options)"
    >
      <template #actions>
        <v-btn
          data-qa="favourite_btn_not_now_btn"
          color="text"
          text
          @click="isOpenedNeedLogin = false"
        >
          {{ t('Не сейчас', options) }}
        </v-btn>
        <v-btn
          data-qa="favourite_btn_login_btn"
          color="primary"
          text
          @click="goToLogin"
        >
          {{ t('Войти', options) }}
        </v-btn>
      </template>
    </CoreDialog>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import useSnackbar from 'src/composables/useSnackbar'
import useContextStore from 'src/store/useContextStore'
import type { CommonContextStore } from 'src/types/context'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import queryParam from 'src/utils/queryParam'
import getLoginLinks from 'src/utils/getLoginLinks'
import apiPatientFavourites from 'src/components/common/FavouriteBtn/api/apiPatientFavourites'
import useFavouriteBtnStore from 'src/components/common/FavouriteBtn/stores/useFavouriteBtnStore'
import type { FavouriteType } from 'src/components/common/FavouriteBtn/types'
import i18next from 'i18next'

const props = defineProps<{
  type: FavouriteType,
  targetId: number,
  size: number | string
}>()

const { context }: CommonContextStore = useContextStore()

const { t, language } = i18next
const options = { ns: language }

const isOpenedNeedLogin = ref(false)
const snackbar = useSnackbar()
const favouriteBtnStore = useFavouriteBtnStore()

const isAnonymous = !context.request.user?.is_authenticated
const isLoading = ref(!isAnonymous)
const isFavourite = ref(false)
const favouriteIconClass = computed(() => (
  isFavourite.value
    ? 'ui-icon-heart-active error--text'
    : 'ui-icon-heart text-info--text'
))

function goToLogin() {
  const loginUrl = new URL(getLoginLinks(context).medtochka)
  const nextQueryParam = loginUrl.searchParams.get('next')

  if (!nextQueryParam) {
    return
  }

  const nextUrl = new URL(nextQueryParam)

  nextUrl.searchParams.set('addToFavourites', String(props.targetId))
  loginUrl.searchParams.set('next', nextUrl.href)

  window.location.href = loginUrl.href
}

function updateFavorite(value: boolean) {
  isFavourite.value = value
  favouriteBtnStore.setFavourite({
    targetId: props.targetId,
    isFavourite: isFavourite.value,
    type: props.type,
  })
}

async function restoreToFavourite() {
  if (isLoading.value) {
    return
  }

  await snackbar.hide()
  // eslint-disable-next-line require-atomic-updates
  isLoading.value = true

  try {
    await apiPatientFavourites.post(props.type, props.targetId)
    updateFavorite(true)
    snackbar.show(t('Восстановлено', options))
  } catch (e) {
    snackbar.show({
      text: t('Произошла ошибка. Попробуйте обновить страницу', options),
      action: {
        btnText: t('Обновить', options),
        onClick: () => {
          window.location.reload()
        },
      },
    })
  } finally {
    // eslint-disable-next-line require-atomic-updates
    isLoading.value = false
  }
}

async function addToFavourites() {
  if (isLoading.value) {
    return
  }

  isLoading.value = true

  try {
    await apiPatientFavourites.post(props.type, props.targetId)
    updateFavorite(true)
    snackbar.show({
      text: t('Добавлено в избранное', options),
      action: {
        btnText: t('Перейти', options),
        onClick: () => {
          window.location.href = `${context.site_mt}/favourites`
        },
      },
    })
  } catch (e) {
    snackbar.show({
      text: t('Произошла ошибка. Попробуйте обновить страницу', options),
      action: {
        btnText: t('Обновить', options),
        onClick: () => {
          window.location.reload()
        },
      },
    })
  } finally {
    // eslint-disable-next-line require-atomic-updates
    isLoading.value = false
  }
}

async function removeFromFavourites() {
  if (isLoading.value) {
    return
  }

  isLoading.value = true

  try {
    await apiPatientFavourites.delete(props.type, props.targetId)
    updateFavorite(false)
    snackbar.show({
      text: t('Удалено из избранного', options),
      action: {
        btnText: t('Отменить', options),
        onClick: restoreToFavourite,
      },
    })
  } catch (e) {
    snackbar.show({
      text: t('Произошла ошибка. Попробуйте обновить страницу', options),
      action: {
        btnText: t('Обновить', options),
        onClick: () => {
          window.location.reload()
        },
      },
    })
  } finally {
    // eslint-disable-next-line require-atomic-updates
    isLoading.value = false
  }
}

async function handleFavouriteClick() {
  if (isLoading.value) {
    return
  }

  if (isAnonymous) {
    isOpenedNeedLogin.value = true

    return
  }

  if (isFavourite.value) {
    await removeFromFavourites()
  } else {
    await addToFavourites()
  }
}

onMounted(async () => {
  if (isAnonymous || (!context.request.user?.patient_profile && context.request.user.is_authenticated)) {
    return
  }

  isLoading.value = true

  const favourites = await favouriteBtnStore.getFavourites(props.targetId, props.type)

  isFavourite.value = !!favourites.get(props.targetId)
  isLoading.value = false

  const addToFavouritesQueryParam = Number(queryParam.get('addToFavourites'))

  if (!isAnonymous && !isFavourite.value && addToFavouritesQueryParam === props.targetId) {
    await addToFavourites()
  }
})
</script>
